/**
 * Command helpers — idempotency keys and stable receipts for mutating calls.
 *
 * Every mutating command sends an Idempotency-Key so the server can
 * deduplicate a resend of the same logical command. The receipt keeps that
 * key, so an explicit retry after a lost response reuses it instead of
 * minting a new command.
 */

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122-shaped v4 fallback for older WebViews. Idempotency keys are
  // correlation identifiers, not secrets, but the API contract still expects
  // a UUID on every mutating request.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
    const random = Math.floor(Math.random() * 16);
    const value = token === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export interface CommandReceipt<TResult> {
  /** Key sent as Idempotency-Key; reuse it to retry without a second effect. */
  idempotencyKey: string;
  /** Client completion time. Metadata only — never ledger time. */
  completedAt: string;
  result: TResult;
}

export interface RunCommandOptions {
  /** Reuse a previous command's key to retry that exact command. */
  idempotencyKey?: string;
}

/** A failed command keeps its key on the original error object. */
export interface CommandFailure extends Error {
  readonly idempotencyKey: string;
}

export function getCommandIdempotencyKey(error: unknown): string | undefined {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) return undefined;
  const key = (error as { idempotencyKey?: unknown }).idempotencyKey;
  return typeof key === 'string' ? key : undefined;
}

class NonErrorCommandFailure extends Error implements CommandFailure {
  constructor(
    public readonly idempotencyKey: string,
    cause: unknown,
  ) {
    super('The command failed.');
    this.name = 'CommandFailure';
    this.cause = cause;
  }
}

function revealCommandKey(error: unknown, idempotencyKey: string): Error {
  if (error instanceof Error) {
    Object.defineProperty(error, 'idempotencyKey', {
      configurable: true,
      enumerable: true,
      value: idempotencyKey,
    });
    return error;
  }
  return new NonErrorCommandFailure(idempotencyKey, error);
}

/**
 * Run a mutating command with a stable idempotency key and return a receipt.
 *
 * No automatic retries happen here: a mutation whose connection drops has an
 * unknown outcome and must first be reconciled with server state. The receipt
 * and failure metadata let an explicit retry reuse the same server command.
 */
export async function runCommand<TResult>(
  execute: (idempotencyKey: string) => Promise<TResult>,
  options: RunCommandOptions = {},
): Promise<CommandReceipt<TResult>> {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  try {
    const result = await execute(idempotencyKey);
    // Consumers may re-check post-value UX eligibility after any confirmed
    // command. The event is only a refresh hint; the server remains the truth
    // source for whether a financial record actually exists.
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('finance:command-confirmed', {
        detail: { idempotencyKey },
      }));
    }
    return {
      idempotencyKey,
      completedAt: new Date().toISOString(),
      result,
    };
  } catch (error) {
    // Preserve ApiClientError identity so callers can still classify offline,
    // auth, validation, and unknown-outcome failures. The key is additional
    // recovery metadata for an explicit retry of this exact command.
    throw revealCommandKey(error, idempotencyKey);
  }
}

export interface RetriableCommandOptions {
  /** Stable identity for the endpoint + payload of one logical command. */
  identity: string;
}

export interface CommandRunner {
  run<TResult>(
    execute: (idempotencyKey: string) => Promise<TResult>,
    options: RetriableCommandOptions,
  ): Promise<CommandReceipt<TResult>>;
  /** Key retained after an unconfirmed/failed command, for support UI. */
  pendingKey(identity?: string): string | undefined;
  /** Abandon the failed command when the user explicitly starts over. */
  reset(): void;
}

/**
 * Keeps one failed logical command's key until it succeeds or its identity
 * changes. This makes a user's explicit retry safe without enabling automatic
 * mutation retries.
 */
export function createCommandRunner(): CommandRunner {
  const failed = new Map<string, string>();
  let latestFailedIdentity: string | undefined;

  return {
    async run<TResult>(
      execute: (idempotencyKey: string) => Promise<TResult>,
      options: RetriableCommandOptions,
    ): Promise<CommandReceipt<TResult>> {
      const retryKey = failed.get(options.identity);
      try {
        const receipt = await runCommand(execute, { idempotencyKey: retryKey });
        failed.delete(options.identity);
        if (latestFailedIdentity === options.identity) latestFailedIdentity = undefined;
        return receipt;
      } catch (error) {
        const idempotencyKey = getCommandIdempotencyKey(error);
        if (idempotencyKey) {
          failed.set(options.identity, idempotencyKey);
          latestFailedIdentity = options.identity;
        }
        throw error;
      }
    },
    pendingKey(identity) {
      return identity
        ? failed.get(identity)
        : latestFailedIdentity
          ? failed.get(latestFailedIdentity)
          : undefined;
    },
    reset() {
      failed.clear();
      latestFailedIdentity = undefined;
    },
  };
}
